import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

const SIDEBAR_COLLAPSED_KEY = "sidebar-collapsed";
const SIDEBAR_WIDTH_VAR = "--sidebar-width";

function setSidebarWidthCssVar(collapsed: boolean) {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(SIDEBAR_WIDTH_VAR, collapsed ? "64px" : "224px");
}

interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsedState] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  });

  const setCollapsed = (value: boolean) => {
    setCollapsedState(value);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value));
    setSidebarWidthCssVar(value);
  };

  const toggleCollapsed = () => setCollapsed(!collapsed);

  useEffect(() => {
    setSidebarWidthCssVar(collapsed);
  }, [collapsed]);

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed, toggleCollapsed }}>{children}</SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within SidebarProvider");
  }
  return context;
}
